import { formatNumber } from './commonFunction';

// Cohesive categorical palette for entitlement usage charts. Leads with the aqfer
// brand blue and moves through teal/green/amber so stacked series stay distinguishable.
export const CHART_PALETTE = [
    '#2C7BE5', // brand blue
    '#00B8A9', // teal
    '#39AFD1', // sky
    '#00D97E', // green
    '#F6C343', // amber
    '#6E84A3', // slate
    '#A66DD4', // purple
    '#FD7E14', // orange
];

// Neutral slate tones so axes and gridlines stay legible in both light and dark themes.
const AXIS_COLOR = '#95AAC9';
const GRID_COLOR = 'rgba(128, 141, 161, 0.18)';
const LIMIT_COLOR = '#E63757';

// Minimum rendered height (px) for a bar segment. A tiny non-zero value in a stacked
// bar can otherwise be sub-pixel tall and impossible to hover for its tooltip.
const MIN_BAR_LENGTH = 3;

export function colorFor(index) {
    return CHART_PALETTE[index % CHART_PALETTE.length];
}

// Common styling applied to every dataset (rounded bars, consistent color).
export function styleDataset(dataset, index) {
    const color = colorFor(index);

    // Drop zero/negative points before applying minBarLength. Chart.js applies the
    // minimum to zero values too, which would draw phantom segments for empty months —
    // filtering first means the floor only lifts real, positive usage into a hoverable
    // sliver (and, for the historical chart, discards mislabelled zero placeholders).
    const yKey = dataset.parsing && dataset.parsing.yAxisKey;
    const data = Array.isArray(dataset.data) && yKey
        ? dataset.data.filter(point => point && Number(point[yKey]) > 0)
        : dataset.data;

    return {
        ...dataset,
        data,
        backgroundColor: color,
        hoverBackgroundColor: color,
        borderColor: color,
        borderWidth: 0,
        borderRadius: 4,
        borderSkipped: false,
        minBarLength: MIN_BAR_LENGTH,
        maxBarThickness: 96,
        categoryPercentage: 0.6,
        barPercentage: 0.9,
    };
}

// Shared x/y scale config for the stacked usage bar charts.
export function barScales() {
    return {
        x: {
            stacked: true,
            grid: { display: false },
            border: { display: false },
            ticks: { color: AXIS_COLOR, font: { size: 11 } },
        },
        y: {
            stacked: true,
            beginAtZero: true,
            grid: { color: GRID_COLOR },
            border: { display: false },
            ticks: {
                color: AXIS_COLOR,
                font: { size: 11 },
                callback: (value) => formatNumber(value),
            },
        },
    };
}

// Dark, rounded tooltip that matches the rest of the portal.
export function tooltipStyle(callbacks = {}) {
    return {
        backgroundColor: 'rgba(18, 38, 63, 0.92)',
        padding: 10,
        cornerRadius: 6,
        titleColor: '#ffffff',
        bodyColor: '#e1e6ec',
        usePointStyle: true,
        callbacks,
    };
}

// Draws a dashed entitlement-limit line with a small right-aligned label, on top of
// the bars. Reads the threshold from options.plugins.limitLine.limit.
export const limitLinePlugin = {
    id: 'limitLine',
    afterDatasetsDraw(chart, args, options) {
        const limit = options.limit;
        if (limit == null || !isFinite(limit)) return;

        const { ctx, chartArea, scales } = chart;
        const y = scales.y.getPixelForValue(limit);
        // Skip when the limit falls outside the visible range.
        if (y < chartArea.top || y > chartArea.bottom) return;

        ctx.save();

        // Dashed line
        ctx.beginPath();
        ctx.moveTo(chartArea.left, y);
        ctx.lineTo(chartArea.right, y);
        ctx.lineWidth = 1.5;
        ctx.strokeStyle = LIMIT_COLOR;
        ctx.setLineDash([6, 4]);
        ctx.stroke();

        // Pill label
        const label = `${options.label || 'Limit'} ${formatNumber(limit)}`;
        ctx.setLineDash([]);
        ctx.font = '600 11px sans-serif';
        const padX = 6;
        const boxH = 18;
        const boxW = ctx.measureText(label).width + padX * 2;
        const boxX = chartArea.right - boxW;
        const boxY = Math.max(chartArea.top, y - boxH - 2);
        const r = 4;

        ctx.beginPath();
        ctx.moveTo(boxX + r, boxY);
        ctx.arcTo(boxX + boxW, boxY, boxX + boxW, boxY + boxH, r);
        ctx.arcTo(boxX + boxW, boxY + boxH, boxX, boxY + boxH, r);
        ctx.arcTo(boxX, boxY + boxH, boxX, boxY, r);
        ctx.arcTo(boxX, boxY, boxX + boxW, boxY, r);
        ctx.closePath();
        ctx.fillStyle = LIMIT_COLOR;
        ctx.fill();

        ctx.fillStyle = '#ffffff';
        ctx.textBaseline = 'middle';
        ctx.fillText(label, boxX + padX, boxY + boxH / 2 + 0.5);

        ctx.restore();
    },
};
